import { AdsbAircraftDetail, AdsbEmergencyType, zIndex } from "@nova/constants"
import { ScrollableArea } from "@nova/component-scrollable-area"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useTrackedAircraft } from "features/map/layers/aircraft-tracking/useTrackedAircraft"
import { useAircraftFullDetail } from "features/map/layers/aircraft-tracking/useAircraftFullDetail"
import { selectActiveAircraftId, setActiveAircraftId } from "../slice"
import classNames from "classnames"
import { useEffect, useState } from "react"
import { formatCoordinate, formatDegrees, formatSpeed } from "@nova/format"
import { planespottersPhotoResponseSchema } from "@nova/schemas"

const useActiveAircraftDetail = () => {
    const activeId = useAppSelector(selectActiveAircraftId)
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const base = useTrackedAircraft({ orgId, id: activeId })
    const { data: detail, isLoading: isFetchingDetail } = useAircraftFullDetail({
        orgId,
        callsign: base?.source === "adsb" ? base.callsign : undefined,
    })
    return {
        isOpen: !!activeId,
        detail: detail ?? null,
        isLoading: isFetchingDetail || (!!activeId && !base),
    }
}

export const AircraftDetailPanel = () => {
    const { isOpen, detail, isLoading } = useActiveAircraftDetail()
    return (
        <div
            className={classNames(
                "absolute left-0 top-0 bottom-0 w-[384px] bg-black-800 backdrop-blur-sm pointer-events-auto overflow-hidden transition-transform duration-300 ease-out",
                {
                    "-translate-x-full": !isOpen,
                    "translate-x-0": isOpen,
                }
            )}
            style={{ zIndex: zIndex.AircraftDetailPanel }}
        >
            <ScrollableArea className="flex flex-col h-full">
                {isOpen && (
                    <>
                        <HeaderSection detail={detail} />
                        {detail ? (
                            <>
                                <PositionSection detail={detail} />
                                <StatusSection detail={detail} />
                                <TelemetrySection detail={detail} />
                            </>
                        ) : (
                            <DetailBody isLoading={isLoading} />
                        )}
                    </>
                )}
            </ScrollableArea>
        </div>
    )
}

const EMERGENCY_LABELS: Record<AdsbEmergencyType, string> = {
    hijack: "Hijack Emergency",
    radioFailure: "Radio Failure",
    general: "General Emergency",
}

const PanelRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center justify-between gap-3 py-0.5">
        <div className="text-gray-300">{label}</div>
        <div className="font-mono text-white text-right">{value}</div>
    </div>
)

const DetailBody = ({ isLoading }: { isLoading: boolean }) => (
    <div className="flex-1 flex items-center justify-center px-6 py-10 text-center text-gray-300">
        {isLoading ? "Loading aircraft details…" : "No details available for this aircraft."}
    </div>
)

const HeaderSection = ({ detail }: { detail: AdsbAircraftDetail | null }) => {
    const dispatch = useAppDispatch()
    const [isLoadingPhoto, setIsLoadingPhoto] = useState(false)
    const [photoUrl, setPhotoUrl] = useState("")
    const [photoLink, setPhotoLink] = useState("")
    const [photographer, setPhotographer] = useState("")
    useEffect(() => {
        if (!detail?.id) return
        let isStale = false
        setIsLoadingPhoto(true)
        setPhotoUrl("")
        setPhotoLink("")
        setPhotographer("")
        fetch(`https://api.planespotters.net/pub/photos/hex/${detail.id}`)
            .then((r) => r.json())
            .then((data) => planespottersPhotoResponseSchema.parse(data))
            .then((data) => {
                if (isStale || "error" in data) return
                setPhotoUrl(data.photos[0]?.thumbnail_large.src ?? "")
                setPhotoLink(data.photos[0]?.link)
                setPhotographer(data.photos[0]?.photographer)
            })
            .catch((error) => {
                if (!isStale) console.error("Failed to fetch planespotters photo", error)
            })
            .finally(() => {
                if (!isStale) setIsLoadingPhoto(false)
            })
        return () => {
            isStale = true
        }
    }, [detail?.id])

    const showPhotoSkeleton = !detail || isLoadingPhoto
    const showPhoto = !showPhotoSkeleton && !!photoUrl
    const showPhotoFallback = !showPhotoSkeleton && !showPhoto
    return (
        <div className="flex flex-col shrink-0">
            <div className="relative w-full h-[255px] overflow-hidden">
                <div
                    className="absolute right-2 top-2 w-9 h-9 bg-black-800 backdrop-blur-sm border border-white-100 flex justify-center items-center rounded-full group cursor-pointer z-20"
                    onClick={() => {
                        dispatch(setActiveAircraftId(null))
                    }}
                >
                    <Icons.SmallIcon
                        Icon={Icons.XClose}
                        className="text-gray-300 group-hover:text-white"
                    />
                </div>
                {showPhotoSkeleton && (
                    <div className="absolute inset-0 z-10 bg-gray-600 animate-pulse duration-75" />
                )}
                {showPhoto && (
                    <>
                        <a href={photoLink} target="__blank" className="w-full h-full block">
                            <img
                                src={photoUrl}
                                alt={`Aircraft ${detail.id}`}
                                className="object-cover min-w-full min-h-full"
                            />
                        </a>
                        {photographer && (
                            <div className="absolute -bottom-0.5 right-0 bg-black-400 backdrop-blur-sm py-0.5 px-1 rounded-tl-md">
                                <small className="text-white-400">© {photographer}</small>
                            </div>
                        )}
                    </>
                )}
                {showPhotoFallback && (
                    <div className="absolute inset-0 z-10 bg-gray-700/80 flex justify-center items-center">
                        <img src="/images/plane.webp" width="250" height="109" alt="" />
                    </div>
                )}
            </div>
            {detail && (
                <div className="flex flex-col gap-1 px-6 py-4">
                    <div className="telegraf text-white text-lg">
                        {detail.operatorAirline ?? "Unknown Airline"}
                    </div>
                    <div className="text-gray-200 text-base">
                        {detail.manufacturer} {detail.model} • {detail.tailNumber ?? "--"}
                    </div>
                    <small className="text-gray-400">{detail.id}</small>
                </div>
            )}
        </div>
    )
}

const PositionSection = ({ detail }: { detail: AdsbAircraftDetail }) => (
    <>
        <div className="w-full h-px bg-white-200 shrink-0" />
        <div className="flex flex-col px-6 pb-4 pt-2 gap-2">
            <div className="pt-1 telegraf text-base text-white">Position</div>
            <PanelRow label="Altitude" value={`${detail.altitude.toFixed(0)} ft`} />
            <PanelRow
                label="Position"
                value={formatCoordinate({ lat: detail.lat, lng: detail.lng })}
            />
        </div>
    </>
)

const StatusSection = ({ detail }: { detail: AdsbAircraftDetail }) => (
    <>
        <div className="w-full h-px bg-white-200 shrink-0" />
        <div className="flex flex-col px-6 pb-4 pt-2 gap-2">
            <div className="flex items-center justify-between pt-1">
                <div className="telegraf text-base text-white">Status</div>
                {detail.emergency && (
                    <span className="text-red-200 border border-red-500 rounded-full px-3 py-1 text-xs">
                        {EMERGENCY_LABELS[detail.emergency]}
                    </span>
                )}
            </div>
            <PanelRow label="Squawk" value={detail.squawk ?? "--"} />
            <PanelRow label="On-ground flag" value={String(detail.onGround)} />
            <PanelRow label="Data source type" value={detail.dataSourceType.toUpperCase()} />
        </div>
    </>
)

const TelemetrySection = ({ detail }: { detail: AdsbAircraftDetail }) => (
    <>
        <div className="w-full h-px bg-white-200 shrink-0" />
        <div className="flex flex-col px-6 pb-4 pt-2 gap-2">
            <div className="pt-1 telegraf text-base text-white">Telemetry</div>
            <PanelRow label="Speed" value={formatSpeed({ value: detail.speed })} />
            <PanelRow label="Track" value={formatDegrees({ value: detail.heading })} />
            <PanelRow label="Vertical rate" value={`${detail.verticalRate} ft/min`} />
        </div>
    </>
)
